import React, { HTMLAttributes, useState } from 'react';
import { FileError, FileRejection, useDropzone } from 'react-dropzone';
import { AlternativeAdd as Add, Trash } from '@td-client-frontend/icons';
import { FileUploadTypes } from '../types';

export interface FileWithPreview extends File {
  preview: string | null;
}

interface Props {
  onUpload: (files: FileWithPreview[]) => void;
  onRemove?: (files: FileWithPreview[]) => void;
  mimeTypes: FileUploadTypes;
  maxFileSizeMB: number; // max file size in MegaBytes
  maxFiles?: number;
  validator?: <T extends File>(file: T) => FileError | FileError[] | null;
}

type NamedFileError = FileError & { fileName: string };

export function FileDropzone({ mimeTypes, maxFileSizeMB, onUpload, onRemove, maxFiles = 1, validator }: Props) {
  const MAX_SIZE_BYTES = maxFileSizeMB * 1000 * 1000;
  const [errors, setErrors] = useState<NamedFileError[]>([]);
  const [files, setFiles] = useState<FileWithPreview[]>([]);

  const onDropAccepted = (acceptedFiles: FileWithPreview[]) => {
    setErrors([]);
    setFiles(acceptedFiles);
    onUpload(acceptedFiles);
  };

  const onDropRejected = (rejectedFiles: FileRejection[]) => {
    const reducer = (namedFileErrors: any[], fileRejection: { file: any; errors: any }) => {
      const { file, errors } = fileRejection;
      errors.forEach((err: any) => namedFileErrors.push({ ...err, fileName: file.name }));
      return namedFileErrors;
    };

    const newErrors: NamedFileError[] = rejectedFiles.reduce(reducer, []);
    setErrors(newErrors);
    setFiles([]);
  };

  const { getRootProps, getInputProps } = useDropzone({
    // @ts-ignore
    onDropAccepted,
    onDropRejected,
    maxSize: MAX_SIZE_BYTES,
    accept: mimeTypes.split(','),
    maxFiles,
    validator
  });

  const removeFile = (fileName: string) => {
    const newFiles = files?.filter((file) => file.name !== fileName);
    setFiles(newFiles);
    onRemove && onRemove(newFiles);
  };

  return (
    <div className='font-effra'>
      <div
        className='flex flex-col items-center justify-center w-full h-64 text-base border-dashed cursor-pointer border-1 border-gray-default dropzone'
        {...(getRootProps() as HTMLAttributes<HTMLDivElement>)}
      >
        <input {...getInputProps({ multiple: false })} data-testid='dropzone' />
        <Add />
        <p>Add File</p>
      </div>
      <div className='pt-5'>
        {errors.map((error, index) => (
          <p key={`error-${error.fileName}-${index}`} className='text-red-500 text-sm'>
            {error.fileName} - {error.message}
          </p>
        ))}
        {files.map((file) => (
          <div key={file.name} className='flex flex-row items-center pt-6 text-base leading-tight'>
            <p className='text-sm break-all'>{file.name}</p>
            <button
              data-testid='dropzone-remove-button'
              onClick={() => {
                removeFile(file.name);
              }}
            >
              <Trash className='fill-current text-primary-500' title='Remove file' height={40} width={40} />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
